/**
 * The wallet option on the screen before a start (Warren, 2026-10-25):
 * "Wallet always shown on the screen before a start, with its balance and
 * either 'Use wallet' or the exact reason it can't be used yet."
 *
 * ONE decision, used three ways, so they cannot disagree:
 *   - canUserStartSession (@evi/billing) resolves a session to wallet mode
 *     only when `usable` is true here;
 *   - GET /chargers/:id/start-preview returns this object unchanged;
 *   - the driver app renders `walletChoice` of it (selectable, or the reason
 *     and the action that would make it selectable).
 *
 * Currency rule: a wallet pays sessions in its own currency. A CAD wallet
 * pays CAD sessions; a CAD wallet never pays a USD one. A USD wallet may pay
 * a CAD session converted (Warren, 2026-10-25T02:52:41Z, option 1), but only
 * when no CAD wallet can, and only at a Bank of Canada rate the caller can
 * pin at start (`usdCadRate`; walletFx.ts). With no rate the USD wallet is
 * not offered at a CAD charger (FX_RATE_UNAVAILABLE). The operator minimum
 * is applied in the session's currency (1000 means US$10.00 at a USD charger
 * and CA$10.00 at a CAD one), against a USD balance's CAD value at the rate.
 * Points session credit is USD only, so it counts toward a USD session's
 * admission and never toward a CAD one.
 *
 * Pure: no I/O. The caller reads the documents and passes them in.
 */
import { normalizeCurrency } from './currency.js';
import { isWalletFxRate, walletFxTopUpCents, walletFxValueCents, WALLET_FX_PAIR, WALLET_FX_SOURCE, } from './walletFx.js';
const TIER_GATED_FOR_CREDIT = new Set(['gold', 'platinum', 'diamond']);
/** A wallet row's currency: missing or null reads as USD (rows written
 *  before the currency field was stamped). Null for an unknown value. */
export function walletRowCurrency(value) {
    if (value === undefined || value === null)
        return 'USD';
    return normalizeCurrency(value);
}
/** The driver's wallets as the option lists them: known currencies only,
 *  one row per currency (balances of duplicate rows are not summed: the
 *  unique index forbids them), USD first. */
export function listWallets(rows) {
    const by = new Map();
    for (const row of rows) {
        const c = walletRowCurrency(row.currency);
        if (!c || by.has(c))
            continue;
        by.set(c, Math.max(0, Math.trunc(row.balance_cents ?? 0)));
    }
    return [...by.entries()]
        .sort(([a], [b]) => (a === b ? 0 : a === 'USD' ? -1 : b === 'USD' ? 1 : a.localeCompare(b)))
        .map(([currency, balanceCents]) => ({ currency, balanceCents }));
}
/** Session credit a USD session may count: the pending pool for Gold+. */
export function tierCreditCents(gamification) {
    const tier = (gamification?.current_rank_code ?? 'silver').toLowerCase();
    if (!TIER_GATED_FOR_CREDIT.has(tier))
        return 0;
    return Math.max(0, gamification?.pending_session_credit_cents ?? 0);
}
export function resolveWalletOption(input) {
    const sessionCurrency = (normalizeCurrency(input.sessionCurrency) ?? String(input.sessionCurrency ?? '').trim().toUpperCase()) || 'USD';
    const supported = normalizeCurrency(sessionCurrency);
    const wallets = listWallets(input.wallets);
    const own = supported ? wallets.find((w) => w.currency === supported) ?? null : null;
    const cash = own?.balanceCents ?? 0;
    // Session credit is USD money: it never counts toward a CAD session.
    const credit = supported === 'USD' ? tierCreditCents(input.gamification) : 0;
    const effective = cash + credit;
    // Since Tier 2 the credit covers only EVI's headroom at stop, so it counts
    // toward admission only when a usable card backs the rest (walletAdmissionCents).
    const cardBacksOverage = input.hasCard && !input.locked;
    const admission = cardBacksOverage ? effective : cash;
    const minimum = Math.max(0, Math.trunc(input.minimumBalanceCents));
    const base = {
        currency: supported ?? sessionCurrency,
        balanceCents: cash,
        creditCents: cardBacksOverage ? credit : 0,
        effectiveBalanceCents: effective,
        sessionCurrency,
        wallets,
        conversion: null,
    };
    const refuse = (reason, extra = {}) => ({
        ...base, usable: false, reason, requiredCents: minimum, shortfallCents: 0, topUpCurrency: null, ...extra,
    });
    if (input.outstandingBalance)
        return refuse('OUTSTANDING_BALANCE');
    // A free session resolves to wallet mode and nothing is debited.
    if (!input.billingExpected) {
        return { ...base, usable: true, reason: null, requiredCents: 0, shortfallCents: 0, topUpCurrency: null };
    }
    if (input.fleetSponsored)
        return refuse('FLEET_SPONSORED');
    if (!supported)
        return refuse('UNSUPPORTED_CURRENCY');
    if (input.crossTenant)
        return refuse('CROSS_TENANT');
    if (admission >= minimum) {
        return { ...base, usable: true, reason: null, requiredCents: minimum, shortfallCents: 0, topUpCurrency: null };
    }
    // A USD wallet at a CAD charger (Warren, 2026-10-25T02:52:41Z, option 1):
    // no CAD wallet can pay, so the USD one may, converted at the rate the
    // start pins. Its value here is the most CAD it pays at that rate, and
    // the operator minimum is checked against that value. No rate, no offer.
    const usd = supported === 'CAD' ? wallets.find((w) => w.currency === 'USD' && w.balanceCents > 0) : undefined;
    if (usd) {
        const rate = isWalletFxRate(input.usdCadRate) ? input.usdCadRate : null;
        if (!rate) {
            // The CAD wallet with money in it stays the one shown (below).
            if (cash === 0) {
                return refuse('FX_RATE_UNAVAILABLE', { currency: 'USD', balanceCents: usd.balanceCents, creditCents: 0 });
            }
        }
        else {
            const conversion = {
                pair: WALLET_FX_PAIR, walletCurrency: 'USD', sessionCurrency: 'CAD', rate: rate.rate,
                rateDate: rate.rateDate, source: WALLET_FX_SOURCE,
                ...(rate.recordedAt ? { recordedAt: rate.recordedAt } : {}),
                valueCents: walletFxValueCents(usd.balanceCents, rate),
            };
            const shown = { currency: 'USD', balanceCents: usd.balanceCents, creditCents: 0, conversion };
            if (conversion.valueCents >= minimum) {
                return { ...base, ...shown, usable: true, reason: null, requiredCents: minimum, shortfallCents: 0, topUpCurrency: null };
            }
            if (cash === 0) {
                return refuse('BELOW_MINIMUM', {
                    ...shown, shortfallCents: walletFxTopUpCents(usd.balanceCents, minimum, rate), topUpCurrency: 'USD',
                });
            }
        }
    }
    const shortfall = minimum - admission;
    // The driver's money is all in another currency: say so, and show it
    // (CAD money at a USD charger: a CAD wallet never pays a USD price).
    const other = cash === 0 ? wallets.find((w) => w.currency !== supported && w.balanceCents > 0) : undefined;
    if (other) {
        return refuse('CURRENCY_MISMATCH', {
            currency: other.currency, balanceCents: other.balanceCents, creditCents: 0,
            shortfallCents: shortfall, topUpCurrency: supported,
        });
    }
    return refuse('BELOW_MINIMUM', { shortfallCents: shortfall, topUpCurrency: supported });
}
/** What the driver app does with the option. Selectable exactly when the
 *  gate would resolve to wallet: there is no other input. */
export function walletChoice(option) {
    return {
        selectable: option.usable === true,
        reason: option.usable ? null : option.reason,
        topUp: !option.usable && option.topUpCurrency
            ? { currency: option.topUpCurrency, amountCents: Math.max(0, option.shortfallCents) }
            : null,
    };
}
//# sourceMappingURL=walletOption.js.map